import { useState } from 'react';
import { QuizQuestion, ImageOption, ImageQuestionQuestion } from '@/types/quiz';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { ImageOptionUpload } from './ImageOptionUpload';

interface QuestionBuilderProps {
  questions: QuizQuestion[];
  onChange: (questions: QuizQuestion[]) => void;
}

export const QuestionBuilder = ({ questions, onChange }: QuestionBuilderProps) => {
  const addQuestion = (type: QuizQuestion['type']) => {
    const id = crypto.randomUUID();
    const baseProps = {
      id,
      question: '',
      explanation: '',
      required: true,
    };

    if (type === 'true-false') {
      onChange([...questions, { ...baseProps, type: 'true-false', correctAnswer: true }]);
    } else if (type === 'multiple-choice') {
      onChange([...questions, { ...baseProps, type: 'multiple-choice', options: ['', ''], correctAnswer: 0 }]);
    } else if (type === 'multi-select') {
      onChange([...questions, { ...baseProps, type: 'multi-select', options: ['', ''], correctAnswers: [] }]);
    } else if (type === 'image-choice') {
      const emptyOptions: ImageOption[] = [
        { imageUrl: '', label: '' },
        { imageUrl: '', label: '' }
      ];
      onChange([...questions, { ...baseProps, type: 'image-choice', options: emptyOptions, correctAnswer: 0 }]);
    } else if (type === 'image-question') {
      onChange([...questions, {
        ...baseProps,
        type: 'image-question',
        mediaUrl: '',
        mediaType: 'image',
        answerType: 'true-false',
        correctAnswer: true,
      } as ImageQuestionQuestion]);
    } else {
      onChange([...questions, { ...baseProps, type: 'open-ended', placeholder: '', maxLength: 500 }]);
    }
  };

  const updateQuestion = (index: number, updates: Partial<QuizQuestion>) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], ...updates } as QuizQuestion;
    onChange(updated);
  };

  const deleteQuestion = (index: number) => {
    onChange(questions.filter((_, i) => i !== index));
  };

  const addOption = (questionIndex: number) => {
    const question = questions[questionIndex];
    if (question.type === 'multiple-choice') {
      updateQuestion(questionIndex, {
        options: [...question.options, ''],
      });
    } else if (question.type === 'multi-select') {
      updateQuestion(questionIndex, {
        options: [...question.options, ''],
      });
    } else if (question.type === 'image-question' && (question.answerType === 'multiple-choice' || question.answerType === 'multi-select')) {
      updateQuestion(questionIndex, {
        options: [...question.options, ''],
      } as Partial<ImageQuestionQuestion>);
    }
  };

  const updateOption = (questionIndex: number, optionIndex: number, value: string) => {
    const question = questions[questionIndex];
    if (question.type === 'multiple-choice' || question.type === 'multi-select') {
      const newOptions = [...question.options];
      newOptions[optionIndex] = value;
      updateQuestion(questionIndex, { options: newOptions });
    } else if (question.type === 'image-question' && (question.answerType === 'multiple-choice' || question.answerType === 'multi-select')) {
      const newOptions = [...question.options];
      newOptions[optionIndex] = value;
      updateQuestion(questionIndex, { options: newOptions } as Partial<ImageQuestionQuestion>);
    }
  };

  const deleteOption = (questionIndex: number, optionIndex: number) => {
    const question = questions[questionIndex];
    if (question.type === 'multiple-choice' && question.options.length > 2) {
      const newOptions = question.options.filter((_, i) => i !== optionIndex);
      let newCorrectAnswer = question.correctAnswer;
      if (optionIndex === question.correctAnswer) {
        newCorrectAnswer = 0;
      } else if (optionIndex < question.correctAnswer) {
        newCorrectAnswer = question.correctAnswer - 1;
      }
      updateQuestion(questionIndex, { options: newOptions, correctAnswer: newCorrectAnswer });
    } else if (question.type === 'multi-select' && question.options.length > 2) {
      const newOptions = question.options.filter((_, i) => i !== optionIndex);
      const newCorrectAnswers = question.correctAnswers
        .filter(idx => idx !== optionIndex)
        .map(idx => idx > optionIndex ? idx - 1 : idx);
      updateQuestion(questionIndex, { options: newOptions, correctAnswers: newCorrectAnswers });
    } else if (question.type === 'image-question' && question.answerType === 'multiple-choice' && question.options.length > 2) {
      const newOptions = question.options.filter((_, i) => i !== optionIndex);
      let newCorrectAnswer = question.correctAnswer;
      if (optionIndex === question.correctAnswer) {
        newCorrectAnswer = 0;
      } else if (optionIndex < question.correctAnswer) {
        newCorrectAnswer = question.correctAnswer - 1;
      }
      updateQuestion(questionIndex, { options: newOptions, correctAnswer: newCorrectAnswer } as Partial<ImageQuestionQuestion>);
    } else if (question.type === 'image-question' && question.answerType === 'multi-select' && question.options.length > 2) {
      const newOptions = question.options.filter((_, i) => i !== optionIndex);
      const newCorrectAnswers = question.correctAnswers
        .filter(idx => idx !== optionIndex)
        .map(idx => idx > optionIndex ? idx - 1 : idx);
      updateQuestion(questionIndex, { options: newOptions, correctAnswers: newCorrectAnswers } as Partial<ImageQuestionQuestion>);
    }
  };

  const toggleMultiSelectCorrect = (questionIndex: number, optionIndex: number) => {
    const question = questions[questionIndex];
    if (question.type === 'multi-select') {
      const currentCorrect = question.correctAnswers || [];
      const newCorrectAnswers = currentCorrect.includes(optionIndex)
        ? currentCorrect.filter(idx => idx !== optionIndex)
        : [...currentCorrect, optionIndex];
      updateQuestion(questionIndex, { correctAnswers: newCorrectAnswers });
    } else if (question.type === 'image-question' && question.answerType === 'multi-select') {
      const currentCorrect = question.correctAnswers || [];
      const newCorrectAnswers = currentCorrect.includes(optionIndex)
        ? currentCorrect.filter(idx => idx !== optionIndex)
        : [...currentCorrect, optionIndex];
      updateQuestion(questionIndex, { correctAnswers: newCorrectAnswers } as Partial<ImageQuestionQuestion>);
    }
  };

  return (
    <div className="space-y-4">
      {questions.map((question, index) => (
        <Card key={question.id}>
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-start gap-4">
              <GripVertical className="h-5 w-5 text-muted-foreground mt-2 cursor-move" />
              <div className="flex-1 space-y-4">
                <div className="flex items-center gap-4">
                  <Label className="min-w-fit">Question {index + 1}</Label>
                  <Input
                    value={question.question}
                    onChange={(e) => updateQuestion(index, { question: e.target.value })}
                    placeholder="Enter your question"
                  />
                </div>

                {question.type === 'true-false' && (
                  <div className="flex items-center gap-4">
                    <Label>Correct Answer</Label>
                    <Select
                      value={question.correctAnswer.toString()}
                      onValueChange={(value) =>
                        updateQuestion(index, { correctAnswer: value === 'true' })
                      }
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">True</SelectItem>
                        <SelectItem value="false">False</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {question.type === 'multiple-choice' && (
                  <div className="space-y-2">
                    <Label>Options</Label>
                    {question.options.map((option, optIndex) => (
                      <div key={optIndex} className="flex items-center gap-2">
                        <Input
                          value={option}
                          onChange={(e) => updateOption(index, optIndex, e.target.value)}
                          placeholder={`Option ${optIndex + 1}`}
                        />
                        <Switch
                          checked={question.correctAnswer === optIndex}
                          onCheckedChange={(checked) => {
                            if (checked) updateQuestion(index, { correctAnswer: optIndex });
                          }}
                        />
                        <Label className="text-sm">Correct</Label>
                        {question.options.length > 2 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteOption(index, optIndex)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button type="button" size="sm" variant="outline" onClick={() => addOption(index)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Option
                    </Button>
                  </div>
                )}

                {question.type === 'multi-select' && (
                  <div className="space-y-2">
                    <Label>Options (select all correct answers)</Label>
                    {question.options.map((option, optIndex) => (
                      <div key={optIndex} className="flex items-center gap-2">
                        <Input
                          value={option}
                          onChange={(e) => updateOption(index, optIndex, e.target.value)}
                          placeholder={`Option ${optIndex + 1}`}
                        />
                        <Checkbox
                          checked={question.correctAnswers?.includes(optIndex) || false}
                          onCheckedChange={() => toggleMultiSelectCorrect(index, optIndex)}
                        />
                        <Label className="text-sm">Correct</Label>
                        {question.options.length > 2 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteOption(index, optIndex)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button type="button" size="sm" variant="outline" onClick={() => addOption(index)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Option
                    </Button>
                  </div>
                )}

                {question.type === 'image-choice' && (
                  <ImageOptionUpload
                    options={question.options}
                    correctAnswer={question.correctAnswer}
                    questionId={question.id}
                    onChange={(options, correctAnswer) =>
                      updateQuestion(index, { options, correctAnswer })
                    }
                  />
                )}

                {question.type === 'image-question' && (
                  <>
                    <div className="space-y-2">
                      <Label>Media (image or video URL)</Label>
                      <div className="flex gap-2 items-center">
                        <Select
                          value={question.mediaType}
                          onValueChange={(value: 'image' | 'video') =>
                            updateQuestion(index, { mediaType: value } as Partial<ImageQuestionQuestion>)
                          }
                        >
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="image">Image</SelectItem>
                            <SelectItem value="video">Video</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          className="flex-1"
                          value={question.mediaUrl}
                          onChange={(e) =>
                            updateQuestion(index, { mediaUrl: e.target.value } as Partial<ImageQuestionQuestion>)
                          }
                          placeholder={question.mediaType === 'video' ? 'Video URL' : 'Image URL'}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Answer type</Label>
                      <Select
                        value={question.answerType}
                        onValueChange={(value: 'true-false' | 'multiple-choice' | 'multi-select') => {
                          if (value === 'true-false') {
                            updateQuestion(index, { answerType: 'true-false', correctAnswer: true } as Partial<ImageQuestionQuestion>);
                          } else if (value === 'multiple-choice') {
                            updateQuestion(index, { answerType: 'multiple-choice', options: ['', ''], correctAnswer: 0 } as Partial<ImageQuestionQuestion>);
                          } else {
                            updateQuestion(index, { answerType: 'multi-select', options: ['', ''], correctAnswers: [] } as Partial<ImageQuestionQuestion>);
                          }
                        }}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true-false">True/False</SelectItem>
                          <SelectItem value="multiple-choice">Multiple Choice</SelectItem>
                          <SelectItem value="multi-select">Multi-Select</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {question.answerType === 'true-false' && (
                      <div className="flex items-center gap-4">
                        <Label>Correct Answer</Label>
                        <Select
                          value={String(question.correctAnswer)}
                          onValueChange={(value) =>
                            updateQuestion(index, { correctAnswer: value === 'true' } as Partial<ImageQuestionQuestion>)
                          }
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="true">True</SelectItem>
                            <SelectItem value="false">False</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {question.answerType === 'multiple-choice' && (
                      <div className="space-y-2">
                        <Label>Options</Label>
                        {question.options.map((option, optIndex) => (
                          <div key={optIndex} className="flex items-center gap-2">
                            <Input
                              value={option}
                              onChange={(e) => updateOption(index, optIndex, e.target.value)}
                              placeholder={`Option ${optIndex + 1}`}
                            />
                            <Switch
                              checked={question.correctAnswer === optIndex}
                              onCheckedChange={(checked) => {
                                if (checked) updateQuestion(index, { correctAnswer: optIndex } as Partial<ImageQuestionQuestion>);
                              }}
                            />
                            <Label className="text-sm">Correct</Label>
                            {question.options.length > 2 && (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => deleteOption(index, optIndex)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button type="button" size="sm" variant="outline" onClick={() => addOption(index)}>
                          <Plus className="mr-2 h-4 w-4" />
                          Add Option
                        </Button>
                      </div>
                    )}
                    {question.answerType === 'multi-select' && (
                      <div className="space-y-2">
                        <Label>Options (select all correct answers)</Label>
                        {question.options.map((option, optIndex) => (
                          <div key={optIndex} className="flex items-center gap-2">
                            <Input
                              value={option}
                              onChange={(e) => updateOption(index, optIndex, e.target.value)}
                              placeholder={`Option ${optIndex + 1}`}
                            />
                            <Checkbox
                              checked={question.correctAnswers?.includes(optIndex) ?? false}
                              onCheckedChange={() => toggleMultiSelectCorrect(index, optIndex)}
                            />
                            <Label className="text-sm">Correct</Label>
                            {question.options.length > 2 && (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => deleteOption(index, optIndex)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button type="button" size="sm" variant="outline" onClick={() => addOption(index)}>
                          <Plus className="mr-2 h-4 w-4" />
                          Add Option
                        </Button>
                      </div>
                    )}
                  </>
                )}

                {question.type === 'open-ended' && (
                  <div className="space-y-2">
                    <div>
                      <Label>Placeholder</Label>
                      <Input
                        value={question.placeholder || ''}
                        onChange={(e) => updateQuestion(index, { placeholder: e.target.value })}
                        placeholder="Enter placeholder text"
                      />
                    </div>
                    <div>
                      <Label>Max Length</Label>
                      <Input
                        type="number"
                        value={question.maxLength || 500}
                        onChange={(e) =>
                          updateQuestion(index, { maxLength: parseInt(e.target.value) })
                        }
                      />
                    </div>
                  </div>
                )}

                <div>
                  <Label>Explanation (Optional)</Label>
                  <Textarea
                    value={question.explanation || ''}
                    onChange={(e) => updateQuestion(index, { explanation: e.target.value })}
                    placeholder="Explain the answer"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={question.required}
                    onCheckedChange={(checked) => updateQuestion(index, { required: checked })}
                  />
                  <Label>Required</Label>
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={() => deleteQuestion(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      <div className="flex gap-2 flex-wrap">
        <Button type="button" variant="outline" onClick={() => addQuestion('true-false')}>
          <Plus className="mr-2 h-4 w-4" />
          True/False
        </Button>
        <Button type="button" variant="outline" onClick={() => addQuestion('multiple-choice')}>
          <Plus className="mr-2 h-4 w-4" />
          Multiple Choice
        </Button>
        <Button type="button" variant="outline" onClick={() => addQuestion('multi-select')}>
          <Plus className="mr-2 h-4 w-4" />
          Multi-Select
        </Button>
        <Button type="button" variant="outline" onClick={() => addQuestion('image-choice')}>
          <Plus className="mr-2 h-4 w-4" />
          Image Choice
        </Button>
        <Button type="button" variant="outline" onClick={() => addQuestion('image-question')}>
          <Plus className="mr-2 h-4 w-4" />
          Image/Question
        </Button>
        <Button type="button" variant="outline" onClick={() => addQuestion('open-ended')}>
          <Plus className="mr-2 h-4 w-4" />
          Open Ended
        </Button>
      </div>
    </div>
  );
};
